import { Box, Button, Typography } from '@material-ui/core';
import { default as classNames } from 'classnames';
import { Filter, SGSCard, SGSColumnFilter, SGSHelpButton, SGSTableCtrl } from 'components';
import { useAppContext } from 'core/contexts';
import { useQueryConf } from 'core/utils/useQuery';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../../../../../pages/Affiliations/Affiliates/Affiliates.module.scss';
import { filtersChanged, pageChanged, pageSizeChanged, sortByChanged } from '../../../redux/searchParamsSliceSubscribers';

import { getSubscriberPrev } from 'core/services/subscriber';
import { useSearchFormContext } from 'features/arretTravail/contexts/SearchFormProvider';

type Props = {
    handleTabChange: Function;
};

const FormSearchSubscriber: React.FC<Props> = ({ handleTabChange }) => {
    const { setSelectedSubscriber, resetSearchParamsContracts, resetSearchParamsAffilie } = useSearchFormContext();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useAppContext();
    let searchParams = useSelector((state: any) => state.searchParamsSubscriber.value);

    /**
     * Handles the change of filter value.
     * Resets the page to 1 and sets the new filter.
     *
     * @param {string} value - The new filter value.
     */
    const handleChangeFilter = (value) => {
        dispatch(pageChanged(1));
        setActiveFilters(value);
    };

    // Load subscribers
    const {
        isLoading: isLoadingSubscribers,
        isFetching,
        data: dataSubscribers,
    } = useQuery(
        [user.login, 'subscribers', searchParams],
        async () => {
            const { page, pageLimit, sortBy, search } = JSON.parse(searchParams);
            const sortPayload = sortBy.length ? createSortPayload(sortBy[0].nomSouscripteur, sortBy[0].asc) : [];
            return await getSubscriberPrev(page, pageLimit, sortPayload, search);
        },
        { ...useQueryConf },
    );

    const subscribers = dataSubscribers?.data ?? [];
    const totalPages = dataSubscribers?.totalPage ?? 0;
    const totalResults = dataSubscribers?.totalResultat ?? 0;

    const isLoadingData = isLoadingSubscribers || isFetching;

    const columns = [
        {
            Header: 'Souscripteur / SIRET',
            accessor: 'nomSouscripteur',
            id: 'nomSouscripteur',
            disableFilters: true,
            minWidth: 250, // Fixed width for Souscripteur
            Cell: ({ row }: { row: any }) => (
                <Box style={{ paddingRight: '10px', paddingLeft: '30px' }}>
                    <Box>
                        <strong>{row.original.nomSouscripteur}</strong>
                    </Box>
                    <Box>{row.original.siret}</Box>
                </Box>
            ),
        },
        {
            Header: 'Adresse',
            id: 'address',
            accessor: (row: Affiliate) => `${row.codePostal} ${row.ville}`, // Combine codePostal and ville
            disableFilters: true,
            minWidth: 300, // Fixed width for Address
            Cell: ({ value }: { value: string }) => <Box style={{ paddingRight: '10px' }}>{value}</Box>,
        },
        {
            Header: 'Action',
            id: 'action',
            accessor: '',
            disableSortBy: true,
            disableFilters: true,
            width: 60, // Fixed width for Action
            Cell: ({ row }: { row: any }) => (
                <Box style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <Button onClick={() => handleRowClick(row.original)} variant="contained" color="primary">
                        Choisir
                    </Button>
                </Box>
            ),
        },
    ];

    const filterableColumnLabels = {
        nomSouscripteur: { value: t('affiliations.affiliates.name'), type: 'input' },
        siret: { value: 'SIRET', type: 'input' },
    };

    const parsedParams = JSON.parse(searchParams);
    const [activeFilters, setActiveFilters] = useState<Filter[]>(parsedParams.search ?? []);

    const methods = useForm({
        defaultValues: {},
    });

    const handleRowClick = (subscriber) => {
        console.log(subscriber.original);
        setSelectedSubscriber(subscriber.original);
        resetSearchParamsContracts();
        resetSearchParamsAffilie();
        handleTabChange(1);
    };

    return (
        <>
            <SGSHelpButton />
            <Box>
                <Box display="flex" className="my-2" justifyContent="left" alignItems="center">
                    <FormProvider {...methods}>
                        <SGSColumnFilter
                            columns={filterableColumnLabels}
                            className="mr-5"
                            filters={activeFilters}
                            onFiltersChange={handleChangeFilter}
                            //disabled={isLoadingData}
                            customLabel={t('filters.on*')}
                        />
                    </FormProvider>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Box sx={{ flex: '1 1 auto' }}> </Box>
                    <Box className={classNames(styles.boxTotalResults)}>
                        <Typography className={styles.totalResults}>
                            {totalResults && totalResults >= 2 ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultats' : ''}
                            {totalResults && totalResults <= 1 ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultat' : ''}
                        </Typography>
                    </Box>
                </Box>

                <SGSCard color="default" className="w-100" contentStyle={styles.cardContent}>
                    <SGSTableCtrl
                        onRowClick={handleRowClick}
                        columns={columns}
                        data={subscribers}
                        loading={isLoadingData}
                        pageCount={totalPages}
                        globalFilter={activeFilters}
                        totalResults={totalResults}
                        filtersChanged={filtersChanged}
                        pageChanged={pageChanged}
                        pageSizeChanged={pageSizeChanged}
                        sortByChanged={sortByChanged}
                        searchParams={useSelector((state: any) => state.searchParamsSubscriber.value)}
                    ></SGSTableCtrl>
                </SGSCard>
            </Box>
        </>
    );
};

export default FormSearchSubscriber;

function createSortPayload(column, order) {
    const payload = {
        column: column,
        order: order ? 'ASC' : 'DESC',
    };
    const encodedPayload = JSON.stringify(payload);
    const sortParam = `${encodedPayload}`;
    return sortParam;
}

type Affiliate = {
    siret: string;
    nomSouscripteur: string;
    codePostal: string;
    ville: string;
    souscripteurCod: string;
    contracts: Array<Contract>;
};

type Contract = {
    contractId: string;
    contractName: string;
};
